import { Component, inject, input, output, Pipe, PipeTransform } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

/**
 * Native port of the AngularJS `incentiveName` filter
 * (account-v1/src/components/incentives/filters/incentiveName.js). Resolves an
 * incentive's display name from `incentive.languages.item[].formalText` by culture
 * (→ 'en' fallback), strips `<p>/<br>` tags, and capitalises the first char
 * (`capitalize(name, true)` = the `isSentence` branch of the native CapitalizePipe).
 *
 * Faithful to v1 incl. the `getByCulture(incentive, 'en')` quirk: the default-culture
 * fallback passes the STRING 'en' as `culture`, so `culture.code` is undefined and it
 * matches a language with a null/undefined code (preserve-bug). Translate resolves via
 * native ngx (byte-identical to the drained `$filter('translate')`).
 */
@Pipe({ name: 'incentiveName', standalone: true })
export class IncentiveNamePipe implements PipeTransform {
  private readonly translate = inject(TranslateService);
  private static readonly NEWLINE_TAGS = /<(p|br)[^><]*>|<\/(p|br)[^><]*>/gm;
  private static readonly DEFAULT_CULTURE = 'en';

  transform(incentive: any, culture?: any): string {
    if (!incentive) return this.notAvailableMessage();
    let name: string =
      this.getByCultureOrDefault(incentive, culture) || this.getFirst(incentive) || this.notAvailableMessage();
    name = name.replace(IncentiveNamePipe.NEWLINE_TAGS, '');
    return this.capitalize(name);
  }

  private notAvailableMessage(): string {
    return this.translate.instant('flipto.components.incentives.IncentiveDescriptionIsNotAvailable');
  }

  private getByCultureOrDefault(incentive: any, culture: any): string | undefined {
    return this.getByCulture(incentive, culture) || this.getByCulture(incentive, IncentiveNamePipe.DEFAULT_CULTURE);
  }

  private getFirst(incentive: any): string | undefined {
    if (incentive.languages) {
      const name = (incentive.languages.item || [])[0];
      if (name) return name.formalText;
    }
    return undefined;
  }

  private getByCulture(incentive: any, culture: any): string | undefined {
    if (incentive.languages) {
      const code = culture ? culture.code : IncentiveNamePipe.DEFAULT_CULTURE;
      const name = (incentive.languages.item || []).find((language: any) => language && language.code == code);
      if (name) return name.formalText;
    }
    return undefined;
  }

  /** `capitalize(name, true)` — isSentence branch: keep source, upper-case first char. */
  private capitalize(source: string): string {
    if (!source) return '';
    return source[0].toUpperCase() + source.slice(1);
  }
}

/**
 * Native read-view port of the AngularJS `ftFinalistIncentives` directive's READ panel:
 * the `.ft-panel` finalist-prize summary (first/second/third place, runner-ups, participant)
 * via the `incentiveName` pipe. Clicking it emits `showModal` so the host opens the native
 * finalist-goals editor.
 */
@Component({
    selector: 'ft-finalist-incentives-ng',
    imports: [TranslateModule, IncentiveNamePipe],
    templateUrl: './ft-finalist-incentives.component.html'
})
export class FtFinalistIncentivesComponent {
    readonly finalistIncentives = input<any>();
    readonly propertyIncentives = input<any[]>([]);
    /** Emitted on panel click — the host opens the native `finalistsGoalsModal`. */
    readonly showModal = output<void>();

    /** v1 `getPropertyIncentive` — find the property incentive by `incentiveUUID`. */
    protected getPropertyIncentive(incentive: any): any {
        if (incentive == null) return '';
        return (this.propertyIncentives() || []).find((pi) => pi.id === incentive.incentiveUUID);
    }
}
